import { useTheme } from '../../hooks/useTheme';
import { Tile, Card, Button, Pill } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { EiapClusterData, EnvironmentCategory } from './EiapClusterInterfaces';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LinkEnmsDialog from './LinkEnmsDialog';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import api from '../../api/axiosInstance';
import { AxiosError } from 'axios';
import { LOADER_DEBOUNCE_MS } from '../../const/global-constants';

/**
 * EIAP Clusters page
 *
 * EIAP Cluster is a grouper concept that encapsulates:
 * - RLO (Radio Logistics Operations)
 * - EIC (Ericsson Intelligent Controller)
 * - EOLM (Ericsson Operations and Lifecycle Manager)
 *
 * Key features:
 * - Display EIAP Clusters as cards
 * - Show endpoint badges (RLO, EIC, EOLM)
 * - Show namespace information
 * - Create/Edit/Delete EIAP Clusters
 * - Link/unlink ENMs to clusters
 * - Filter by environment category
 */
function EiapClusters() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  // State for EIAP clusters from backend
  const [eiapClusters, setEiapClusters] = useState<EiapClusterData[]>([]);
  const [filteredEiapClusters, setFilteredEiapClusters] = useState<EiapClusterData[]>([]);

  // Environment category filtering
  const categoryPills: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];
  const [filteredCategories, setFilteredCategories] = useState<EnvironmentCategory[]>(categoryPills);

  // Link ENMs Dialog state
  const [linkEnmsDialogOpen, setLinkEnmsDialogOpen] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState<string>('');

  // Fetch EIAP clusters from backend on component mount
  useEffect(() => {
    fetchEiapClusters();
  }, []);

  useEffect(() => {
    if (filteredCategories.length > 0) {
      const filtered = eiapClusters.filter((cluster) => filteredCategories.includes(cluster.category))
      setFilteredEiapClusters(filtered);
    } else {
      setFilteredEiapClusters(eiapClusters);
    }
  }, [filteredCategories]);

  const fetchEiapClusters = async () => {
    try {
      showLoader();
      const response = await api.get('/api/eiap-cluster');
      setEiapClusters(response.data);
      setFilteredEiapClusters(response.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error - EIAP Clusters',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching EIAP clusters.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => { hideLoader(); }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleCreateCluster = () => {
    navigate('/integrations/eiap-clusters/new');
  };

  const handleEditCluster = (clusterId: string) => {
    navigate(`/integrations/eiap-clusters/edit/${clusterId}`);
  };

  const handleDeleteCluster = async (clusterId: string) => {
    try {
      showLoader();
      await api.delete(`/api/eiap-cluster/${clusterId}`);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: 'EIAP Cluster deleted successfully!',
          timestamp: new Date(),
          color: 'green',
        },
      ]);
      // Refresh the list after deletion
      await fetchEiapClusters();
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while deleting EIAP cluster.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    } finally {
      setTimeout(() => { hideLoader(); }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleLinkEnms = (clusterId: string) => {
    setSelectedClusterId(clusterId);
    setLinkEnmsDialogOpen(true);
  };

  const handleSaveLinkEnms = async (clusterId: string, linkedEnmIds: string[]) => {
    try {
      showLoader();
      await api.put(`/api/eiap-cluster/${clusterId}/link-enms`, { enmIds: linkedEnmIds });
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `ENM links updated successfully for cluster!`,
          timestamp: new Date(),
          color: 'green',
        },
      ]);
      // Refresh the list after linking
      await fetchEiapClusters();
      setLinkEnmsDialogOpen(false);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while linking ENMs.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    } finally {
      setTimeout(() => { hideLoader(); }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleCloseLinkEnmsDialog = () => {
    setLinkEnmsDialogOpen(false);
    setSelectedClusterId('');
  };

  const getStatusColor = (status: EiapClusterData['status']) => {
    switch (status) {
      case 'online':
        return '#28a745'; // Green
      case 'offline':
        return '#6c757d'; // Gray
      case 'error':
        return '#dc3545'; // Red
      default:
        return '#6c757d';
    }
  };

  const getCategoryTagClass = (category: EiapClusterData['category']) => {
    switch (category) {
      case 'Production':
        return 'red';
      case 'Staging':
        return 'yellow';
      case 'Development':
        return 'blue';
      case 'Testing':
        return 'green';
      default:
        return 'purple';
    }
  };

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper
            component={Tile}
            title="EIAP Clusters"
            id="eiap-clusters-tile"
          >
            {/* Header with Create Button */}
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
              <ReactEDSWrapper component={Button} primary onClick={handleCreateCluster}>
                <i className="icon icon-plus" /> Create New Cluster
              </ReactEDSWrapper>
            </div>
            {/* Category Filter Pills */}
            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {categoryPills.map((category) => (
                <ReactEDSWrapper
                  key={category}
                  component={Pill}
                  toggle
                  unselected={!filteredCategories.includes(category)}
                  onToggle={(unselected) => {
                    if (unselected) {
                      setFilteredCategories([...filteredCategories, category]);
                    } else if (filteredCategories.length === categoryPills.length) {
                      setFilteredCategories([category]);
                    } else if (filteredCategories.length === 1) {
                      setFilteredCategories(categoryPills);
                    } else {
                      setFilteredCategories(filteredCategories.filter((cat) => cat !== category));
                    }
                  }}
                >
                  {category}
                </ReactEDSWrapper>
              ))}
              <div style={{ color: '#6a6a6a' }}>
                {filteredEiapClusters.length} EIAP Cluster{filteredEiapClusters.length !== 1 ? 's' : ''}
              </div>
              {filteredCategories.length < categoryPills.length && (
                <a
                  href="javascript:void(0);"
                  onClick={() => setFilteredCategories(categoryPills)}
                  style={{ marginLeft: '8px', fontSize: '14px' }}
                >
                  Select all
                </a>
              )}
            </div>

            <div className="row">
              {filteredEiapClusters
                .map((cluster) => (
                <div key={cluster.id} className="column sm-12 md-6 xl-4">
                  <ReactEDSWrapper
                    component={Card}
                    title={cluster.name}
                  >
                    {/* Category Badge */}
                    <div style={{ marginBottom: '12px' }}>
                      <kbd className={`tag big ${getCategoryTagClass(cluster.category)}`}>
                        {cluster.category}
                      </kbd>
                    </div>

                    {/* Status Indicator */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <i
                        className="icon icon-circle"
                        style={{
                          fontSize: '8px',
                          color: getStatusColor(cluster.status),
                          marginRight: '8px'
                        }}
                      />
                      <span style={{ textTransform: 'capitalize', color: '#6a6a6a' }}>{cluster.status}</span>
                    </div>

                    {/* Endpoint Badges */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', marginBottom: '12px' }}>
                      {cluster.endpoints.map((endpoint: any, index: number) => (
                        <ReactEDSWrapper key={`${cluster.id}-${endpoint.targetEnvToolType.name}-${index}`} component={Pill}>
                          <i className="icon icon-link" /> {endpoint.targetEnvToolType.name}
                        </ReactEDSWrapper>
                      ))}
                    </div>

                    {/* Description */}
                    {cluster.description && (
                      <p style={{ marginBottom: '12px', color: '#6a6a6a' }}>{cluster.description}</p>
                    )}

                    {/* Linked ENMs Info */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <i className="icon icon-network" style={{ marginRight: '8px', color: '#6a6a6a' }} />
                      <span style={{ color: '#6a6a6a' }}>
                        {`${cluster.endpoints?.filter(ep => ep.targetEnvToolType?.name === 'ENM')?.length || 0} ENM${(cluster.endpoints?.filter(ep => ep.targetEnvToolType?.name === 'ENM')?.length || 0) !== 1 ? 's' : ''} linked`}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div>
                      <ReactEDSWrapper
                        component={Button}
                        variant="secondary"
                        onClick={() => cluster.id && handleLinkEnms(cluster.id)}
                      >
                        Link ENMs
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Button}
                        variant="secondary"
                        onClick={() => cluster.id && handleEditCluster(cluster.id)}
                      >
                        <i className="icon icon-edit" /> Edit
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Button}
                        variant="secondary"
                        onClick={() => cluster.id && handleDeleteCluster(cluster.id)}
                      >
                        <i className="icon icon-trashcan" /> Delete
                      </ReactEDSWrapper>
                    </div>
                  </ReactEDSWrapper>
                </div>
              ))}
            </div>
          </ReactEDSWrapper>
        </div>
      </div>

      {/* Link ENMs Dialog */}
      <LinkEnmsDialog
        open={linkEnmsDialogOpen}
        clusterId={selectedClusterId}
        onSave={handleSaveLinkEnms}
        onClose={handleCloseLinkEnmsDialog}
      />
    </div>
  );
}

export default EiapClusters;
